import type {
  SuggestionTreeRequest,
  SuggestionTreeResponse,
} from '../../../../domains/orgchart/models/agentRequests';
import { stripLeakedToolResults } from '../leakedToolResults';

type RequestFn = (input: string, init?: RequestInit) => Promise<Response>;

/** Value-free diagnostics: never carry prompts or upstream response bodies. */
export class AgentRequestError extends Error {
  constructor(
    readonly phase: 'http' | 'stream',
    readonly status: number,
    readonly requestId: string | null,
    readonly code: string,
  ) {
    super(`Assistant request failed (${code})`);
    this.name = 'AgentRequestError';
  }
}

export async function createSuggestionTree(
  request: SuggestionTreeRequest,
  requestFn: RequestFn,
): Promise<SuggestionTreeResponse> {
  const response = await requestFn('/api/ai/agent/suggestion-tree', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const message = await response.text().catch(() => 'Suggestion tree request failed');
    throw new Error(message || 'Suggestion tree request failed');
  }

  return response.json() as Promise<SuggestionTreeResponse>;
}

interface SuggestionTreeStreamEventBranch {
  type: 'branch';
  branch: { option: string; question: string; options: string[] };
}

interface SuggestionTreeStreamEventNext {
  type: 'next';
  parentOption: string;
  next: { option: string; question: string; options: string[] };
}

interface SuggestionTreeStreamEventDone {
  type: 'done';
  result: SuggestionTreeResponse;
}

interface SuggestionTreeStreamEventError {
  type: 'error';
  message?: string;
}

type SuggestionTreeStreamEvent =
  | SuggestionTreeStreamEventBranch
  | SuggestionTreeStreamEventNext
  | SuggestionTreeStreamEventDone
  | SuggestionTreeStreamEventError
  | { type: 'trace'; step?: string };

export async function streamSuggestionTree(
  request: SuggestionTreeRequest,
  handlers: {
    onBranch?: (branch: { option: string; question: string; options: string[] }) => void;
    onNext?: (parentOption: string, next: { option: string; question: string; options: string[] }) => void;
  },
  requestFn: RequestFn,
): Promise<SuggestionTreeResponse> {
  const response = await requestFn('/api/ai/agent/suggestion-tree/stream', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(request),
  });

  if (!response.ok || !response.body) {
    const message = await response.text().catch(() => 'Suggestion tree stream request failed');
    throw new Error(message || 'Suggestion tree stream request failed');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finalResult: SuggestionTreeResponse | null = null;
  const partialBranches = new Map<string, { option: string; question: string; options: string[]; next: Array<{ option: string; question: string; options: string[] }> }>();
  let sawStreamError = false;
  let streamErrorMessage: string | null = null;

  const upsertBranch = (branch: { option: string; question: string; options: string[] }) => {
    const existing = partialBranches.get(branch.option);
    if (existing) {
      existing.question = branch.question;
      existing.options = branch.options;
      return;
    }
    partialBranches.set(branch.option, {
      option: branch.option,
      question: branch.question,
      options: branch.options,
      next: [],
    });
  };

  const appendNext = (
    parentOption: string,
    next: { option: string; question: string; options: string[] },
  ) => {
    const parent = partialBranches.get(parentOption);
    if (!parent) {
      partialBranches.set(parentOption, {
        option: parentOption,
        question: '',
        options: [],
        next: [next],
      });
      return;
    }
    const idx = parent.next.findIndex((n) => n.option === next.option);
    if (idx >= 0) {
      parent.next[idx] = next;
    } else {
      parent.next.push(next);
    }
  };

  const processBlock = (block: string) => {
    if (!block.trim()) return;
    const dataLines = block
      .split('\n')
      .filter((line) => line.startsWith('data:'))
      .map((line) => line.slice(5).trimStart());
    if (dataLines.length === 0) return;

    const jsonText = dataLines.join('\n');
    let event: SuggestionTreeStreamEvent;
    try {
      event = JSON.parse(jsonText) as SuggestionTreeStreamEvent;
    } catch {
      return;
    }

    if (event.type === 'branch' && event.branch) {
      upsertBranch(event.branch);
      handlers.onBranch?.(event.branch);
    } else if (event.type === 'next' && event.parentOption && event.next) {
      appendNext(event.parentOption, event.next);
      handlers.onNext?.(event.parentOption, event.next);
    } else if (event.type === 'done' && event.result) {
      finalResult = event.result;
    } else if (event.type === 'error') {
      sawStreamError = true;
      streamErrorMessage = event.message ?? 'Suggestion tree stream failed';
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');

    let boundary = buffer.indexOf('\n\n');
    while (boundary !== -1) {
      const block = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      boundary = buffer.indexOf('\n\n');
      processBlock(block);
    }
  }

  // Process any trailing block without final separator.
  if (buffer.trim()) {
    processBlock(buffer);
  }

  if (finalResult) {
    return finalResult;
  }

  if (partialBranches.size > 0) {
    return {
      branches: Array.from(partialBranches.values()).map((b) => ({
        option: b.option,
        question: b.question,
        options: b.options,
        next: b.next,
      })),
    };
  }

  if (sawStreamError) {
    throw new Error(streamErrorMessage ?? 'Suggestion tree stream failed');
  }

  throw new Error('Suggestion tree stream ended without usable result');
}

export interface AgentChatStreamMessage {
  role: 'system' | 'user' | 'assistant' | 'tool';
  content: string | Array<{ toolCallId: string; toolName: string; result: string }>;
  toolCalls?: Array<{ id?: string; name: string; params: Record<string, unknown> }>;
}

export interface AgentChatStreamRequestPayload {
  endpoint: string;
  requestFn: RequestFn;
  body: {
    messages: AgentChatStreamMessage[];
    diagramType: string;
    diagramSummary: {
      nodeCount: number;
      edgeCount: number;
      fields: string[];
      departments?: string[];
      availableTitles?: string[];
      contextScope?: {
        requiredFields: string[];
        clarifyingQuestionScope: string;
        contextShape: string;
      };
      contextPayload?: Record<string, unknown>;
    };
    availableTools: string[];
    toolDefinitions: Array<{
      name: string;
      description?: string;
      parameters: Record<string, unknown>;
      tier: string;
      category: string;
    }>;
    uiLocale?: string;
  };
  signal?: AbortSignal;
  onTrace?: (step: string) => void;
  onTextDelta?: (delta: string) => void;
}

export async function streamAgentChatTurn(
  payload: AgentChatStreamRequestPayload,
): Promise<{ text: string; toolCalls: Array<{ id?: string; name: string; params: Record<string, unknown> }> }> {
  const response = await payload.requestFn(payload.endpoint, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload.body),
    signal: payload.signal,
  });

  if (!response.ok) {
    const data: unknown = await response.json().catch(() => null);
    const featureDisabled = response.status === 403 && typeof data === 'object' && data !== null
      && 'code' in data && data.code === 'FEATURE_DISABLED';
    throw new AgentRequestError('http', response.status, response.headers.get('x-request-id'),
      featureDisabled ? 'FEATURE_DISABLED' : `HTTP_${response.status}`);
  }

  if (!response.body) {
    const data = await response.json().catch(() => ({}));
    return {
      text: String((data as { text?: string }).text ?? ''),
      toolCalls: ((data as { toolCalls?: Array<{ id?: string; name: string; params: Record<string, unknown> }> }).toolCalls ?? []),
    };
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finalPayload: { text?: string; toolCalls?: Array<{ id?: string; name: string; params: Record<string, unknown> }> } | null = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');

    let boundary = buffer.indexOf('\n\n');
    while (boundary !== -1) {
      const block = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      boundary = buffer.indexOf('\n\n');

      if (!block.trim()) continue;
      const dataLines = block
        .split('\n')
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice(5).trimStart());
      if (dataLines.length === 0) continue;

      const jsonText = dataLines.join('\n');
      let event: { type: string; step?: string; delta?: string; message?: string; payload?: { text?: string; toolCalls?: Array<{ id?: string; name: string; params: Record<string, unknown> }> } };
      try {
        event = JSON.parse(jsonText);
      } catch {
        continue;
      }

      if (event.type === 'trace' && typeof event.step === 'string') {
        payload.onTrace?.(event.step);
      }
      if (event.type === 'text-delta' && typeof event.delta === 'string') {
        payload.onTextDelta?.(event.delta);
      }
      if (event.type === 'final') {
        finalPayload = event.payload ?? null;
      }
      if (event.type === 'error') {
        throw new AgentRequestError('stream', response.status, response.headers.get('x-request-id'), 'STREAM_ERROR');
      }
    }
  }

  if (!finalPayload) {
    throw new AgentRequestError('stream', response.status, response.headers.get('x-request-id'), 'STREAM_INCOMPLETE');
  }

  return {
    text: stripLeakedToolResults(finalPayload.text ?? ''),
    toolCalls: finalPayload.toolCalls ?? [],
  };
}
